import React, { useState, useEffect } from 'react';
import { Preferences } from '../types';
import {
  StepContainer,
  StepTitle,
  StepDescription,
  PreferencesContainer,
  PreferenceSection,
  SectionTitle,
  SectionDescription,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheckbox,
  FormCheckboxGroup,
  FormCheckboxItem,
  RangeContainer,
  RangeInput,
  RangeValue,
  DistanceSlider,
  DealBreakerInput,
  DealBreakerList,
  DealBreakerItem,
  RemoveButton,
  AddButton,
  ValidationMessage,
  PreferencePreview
} from '../../../styles/Profile/ProfileSetup.styles';

interface PreferencesStepProps {
  data: Partial<Preferences>;
  onChange: (data: Preferences, isValid: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' }
];

const INTEREST_OPTIONS = [
  { value: 'dating', label: 'Dating' },
  { value: 'friendship', label: 'Friendship' },
  { value: 'serious', label: 'Serious Relationship' },
  { value: 'casual', label: 'Casual Relationship' }
];

const DEAL_BREAKER_SUGGESTIONS = [
  'Smoking',
  'Heavy drinking',
  'Drug use',
  'Not wanting children',
  'Different religious beliefs',
  'Different political views',
  'Long distance relationship',
  'Not interested in marriage',
  'Not interested in commitment',
  'Different lifestyle choices'
];

export const PreferencesStep: React.FC<PreferencesStepProps> = ({
  data,
  onChange,
  onNext,
  onBack,
  isFirstStep,
  isLastStep
}) => {
  const [preferences, setPreferences] = useState<Preferences>({
    ageRange: { min: 18, max: 65 },
    distance: 25,
    genderPreference: [],
    interestedIn: [],
    dealBreakers: [],
    ...data
  });

  const [newDealBreaker, setNewDealBreaker] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validatePreferences = (): string[] => {
    const errors: string[] = [];
    
    if (preferences.ageRange.min >= preferences.ageRange.max) {
      errors.push('Minimum age must be less than maximum age');
    }
    
    if (preferences.ageRange.min < 18) {
      errors.push('Minimum age must be at least 18');
    }
    
    if (preferences.ageRange.max > 100) {
      errors.push('Maximum age must be less than 100');
    }
    
    if (preferences.distance < 1) {
      errors.push('Distance must be at least 1 mile');
    }
    
    if (preferences.distance > 100) {
      errors.push('Distance must be less than 100 miles');
    }
    
    if (preferences.genderPreference.length === 0) {
      errors.push('Please select at least one gender preference');
    }
    
    if (preferences.interestedIn.length === 0) {
      errors.push('Please select at least one relationship type');
    }
    
    return errors;
  };

  const handleAgeRangeChange = (field: 'min' | 'max', value: number) => {
    setPreferences(prev => ({
      ...prev,
      ageRange: {
        ...prev.ageRange,
        [field]: value
      }
    }));
  };

  const handleDistanceChange = (value: number) => {
    setPreferences(prev => ({
      ...prev,
      distance: value
    }));
  };

  const handleGenderPreferenceChange = (gender: string, checked: boolean) => {
    setPreferences(prev => ({
      ...prev,
      genderPreference: checked
        ? [...prev.genderPreference, gender as any]
        : prev.genderPreference.filter(g => g !== gender)
    }));
  };

  const handleInterestedInChange = (interest: string, checked: boolean) => {
    setPreferences(prev => ({
      ...prev,
      interestedIn: checked
        ? [...prev.interestedIn, interest as any]
        : prev.interestedIn.filter(i => i !== interest)
    }));
  };

  const handleAddDealBreaker = () => {
    if (newDealBreaker.trim() && !preferences.dealBreakers.includes(newDealBreaker.trim())) {
      setPreferences(prev => ({
        ...prev,
        dealBreakers: [...prev.dealBreakers, newDealBreaker.trim()]
      }));
      setNewDealBreaker('');
    }
  };

  const handleRemoveDealBreaker = (dealBreaker: string) => {
    setPreferences(prev => ({
      ...prev,
      dealBreakers: prev.dealBreakers.filter(db => db !== dealBreaker)
    }));
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!preferences.dealBreakers.includes(suggestion)) {
      setPreferences(prev => ({
        ...prev,
        dealBreakers: [...prev.dealBreakers, suggestion]
      }));
    }
  };

  useEffect(() => {
    const errors = validatePreferences();
    setValidationErrors(errors);
    const isValid = errors.length === 0;
    onChange(preferences, isValid);
  }, [preferences]);

  return (
    <StepContainer>
      <StepTitle>Set your preferences</StepTitle>
      <StepDescription>
        Tell us what you're looking for so we can show you the most compatible matches.
      </StepDescription>

      <PreferencesContainer>
        <PreferenceSection>
          <SectionTitle>Age Range</SectionTitle>
          <SectionDescription>
            What age range are you interested in?
          </SectionDescription>
          
          <RangeContainer>
            <FormGroup>
              <FormLabel>Minimum Age</FormLabel>
              <RangeInput
                type="number"
                min="18"
                max="100"
                value={preferences.ageRange.min}
                onChange={(e) => handleAgeRangeChange('min', parseInt(e.target.value))}
              />
              <RangeValue>{preferences.ageRange.min} years</RangeValue>
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Maximum Age</FormLabel>
              <RangeInput
                type="number"
                min="18"
                max="100"
                value={preferences.ageRange.max}
                onChange={(e) => handleAgeRangeChange('max', parseInt(e.target.value))}
              />
              <RangeValue>{preferences.ageRange.max} years</RangeValue>
            </FormGroup>
          </RangeContainer>
        </PreferenceSection>

        <PreferenceSection>
          <SectionTitle>Distance</SectionTitle>
          <SectionDescription>
            How far are you willing to travel for a date?
          </SectionDescription>
          
          <FormGroup>
            <DistanceSlider
              type="range"
              min="1"
              max="100"
              value={preferences.distance}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDistanceChange(parseInt(e.target.value))}
            />
            <RangeValue>{preferences.distance} miles</RangeValue>
          </FormGroup>
        </PreferenceSection>

        <PreferenceSection>
          <SectionTitle>Gender Preference</SectionTitle>
          <SectionDescription>
            What genders are you interested in?
          </SectionDescription>
          
          <FormCheckboxGroup>
            {GENDER_OPTIONS.map(option => (
              <FormCheckboxItem key={option.value}>
                <FormCheckbox
                  type="checkbox"
                  checked={preferences.genderPreference.includes(option.value as any)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleGenderPreferenceChange(option.value, e.target.checked)}
                />
                <FormLabel>{option.label}</FormLabel>
              </FormCheckboxItem>
            ))}
          </FormCheckboxGroup>
        </PreferenceSection>

        <PreferenceSection>
          <SectionTitle>Relationship Type</SectionTitle>
          <SectionDescription>
            What type of relationship are you looking for?
          </SectionDescription>
          
          <FormCheckboxGroup>
            {INTEREST_OPTIONS.map(option => (
              <FormCheckboxItem key={option.value}>
                <FormCheckbox
                  type="checkbox"
                  checked={preferences.interestedIn.includes(option.value as any)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInterestedInChange(option.value, e.target.checked)}
                />
                <FormLabel>{option.label}</FormLabel>
              </FormCheckboxItem>
            ))}
          </FormCheckboxGroup>
        </PreferenceSection>

        <PreferenceSection>
          <SectionTitle>Deal Breakers</SectionTitle>
          <SectionDescription>
            What are your absolute deal breakers? (Optional)
          </SectionDescription>
          
          <DealBreakerInput>
            <FormInput
              type="text"
              placeholder="Add a deal breaker..."
              value={newDealBreaker}
              onChange={(e) => setNewDealBreaker(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddDealBreaker()}
            />
            <AddButton onClick={handleAddDealBreaker}>Add</AddButton>
          </DealBreakerInput>
          
          <DealBreakerList>
            {preferences.dealBreakers.map(dealBreaker => (
              <DealBreakerItem key={dealBreaker}>
                {dealBreaker}
                <RemoveButton onClick={() => handleRemoveDealBreaker(dealBreaker)}>
                  ×
                </RemoveButton>
              </DealBreakerItem>
            ))}
          </DealBreakerList>
          
          <div style={{ marginTop: '1rem' }}>
            <h4>Suggestions:</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {DEAL_BREAKER_SUGGESTIONS.map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  disabled={preferences.dealBreakers.includes(suggestion)}
                  style={{
                    background: preferences.dealBreakers.includes(suggestion) 
                      ? 'rgba(255, 255, 255, 0.2)' 
                      : 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '16px',
                    cursor: preferences.dealBreakers.includes(suggestion) ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem',
                    opacity: preferences.dealBreakers.includes(suggestion) ? 0.5 : 1
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </PreferenceSection>
      </PreferencesContainer>

      {validationErrors.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {validationErrors.map((error, index) => (
            <ValidationMessage key={index}>{error}</ValidationMessage>
          ))}
        </div>
      )}

      {validationErrors.length === 0 && (
        <PreferencePreview>
          <h4>Your Preferences Summary:</h4>
          <p>Age: {preferences.ageRange.min}-{preferences.ageRange.max} years</p>
          <p>Distance: {preferences.distance} miles</p>
          <p>Gender: {preferences.genderPreference.join(', ')}</p>
          <p>Looking for: {preferences.interestedIn.join(', ')}</p>
          {preferences.dealBreakers.length > 0 && (
            <p>Deal breakers: {preferences.dealBreakers.join(', ')}</p>
          )}
        </PreferencePreview>
      )}
    </StepContainer>
  );
};
